import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, ArrowRight, HelpCircle } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PricingPlan {
  name: string;
  description: string;
  price: string;
  period: string;
  icon: React.ElementType;
  features: PlanFeature[];
  popular?: boolean;
  ctaText: string;
  ctaLink: string;
}

const plans: PricingPlan[] = [
  {
    name: 'Explorer',
    description: 'Perfect for getting started with online learning',
    price: 'Free',
    period: 'forever',
    icon: Sparkles,
    features: [
      { text: 'Access to free courses', included: true },
      { text: 'Course previews', included: true },
      { text: 'Community forum access', included: true },
      { text: 'Email support', included: true },
      { text: 'Premium courses', included: false },
      { text: 'Certificates', included: false },
      { text: 'Live sessions', included: false },
      { text: '1-on-1 mentoring', included: false },
    ],
    ctaText: 'Get Started',
    ctaLink: '/register',
  },
  {
    name: 'Professional',
    description: 'For serious learners ready to advance their careers',
    price: '$29',
    period: 'per month',
    icon: Zap,
    popular: true,
    features: [
      { text: 'Everything in Explorer', included: true },
      { text: 'Unlimited premium courses', included: true },
      { text: 'Certificates of completion', included: true },
      { text: 'Live session access', included: true },
      { text: 'Priority support', included: true },
      { text: 'Downloadable resources', included: true },
      { text: '1-on-1 mentoring', included: false },
      { text: 'Custom learning paths', included: false },
    ],
    ctaText: 'Start Free Trial',
    ctaLink: '/register?plan=professional',
  },
  {
    name: 'Enterprise',
    description: 'For teams and organizations seeking excellence',
    price: '$99',
    period: 'per user/month',
    icon: Crown,
    features: [
      { text: 'Everything in Professional', included: true },
      { text: 'Unlimited team seats', included: true },
      { text: '1-on-1 mentoring sessions', included: true },
      { text: 'Custom learning paths', included: true },
      { text: 'Admin dashboard', included: true },
      { text: 'Analytics & reporting', included: true },
      { text: 'API access', included: true },
      { text: 'Dedicated account manager', included: true },
    ],
    ctaText: 'Contact Sales',
    ctaLink: '/contact?subject=enterprise',
  },
];

const faqs = [
  {
    question: 'Can I switch plans later?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'Absolutely! Our Professional plan comes with a 14-day free trial. No credit card required to start.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for Enterprise plans.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes, you can cancel your subscription at any time. You\'ll continue to have access until the end of your billing period.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 30-day money-back guarantee for all paid plans. If you\'re not satisfied, contact us for a full refund.',
  },
  {
    question: 'What happens to my certificates if I cancel?',
    answer: 'Your earned certificates are yours forever! They remain valid and accessible even after cancellation.',
  },
];

export default function PricingPage() {
  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[350px] bg-coral/10 rounded-full blur-[100px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div variants={fadeUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                             bg-slate/15 border border-slate/25
                             text-slate-light text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Simple, Transparent Pricing</span>
              </span>
            </motion.div>

            <motion.h1 
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
            >
              Invest in Your{' '}
              <span className="text-gradient">Future</span>
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="mt-6 text-lg text-white/50 max-w-2xl mx-auto"
            >
              Choose the perfect plan for your learning journey. All plans include 
              access to our premium learning platform and community.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 sm:pb-20">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative card ${plan.popular ? 'border-coral/40' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 rounded-full text-xs font-semibold
                                   bg-gradient-to-r from-coral to-coral-light text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center pb-6 border-b border-white/10">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4
                                ${plan.popular 
                                  ? 'bg-coral/20 border border-coral/30' 
                                  : 'bg-slate/20 border border-slate/30'}`}>
                    <plan.icon className={`w-7 h-7 ${plan.popular ? 'text-coral' : 'text-slate-light'}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  <p className="mt-2 text-white/40 text-sm">{plan.description}</p>
                  <div className="mt-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-white/40 ml-2">/{plan.period}</span>
                  </div>
                </div>

                <ul className="py-6 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5
                                      ${feature.included 
                                        ? 'bg-success/20 text-success' 
                                        : 'bg-white/5 text-white/30'}`}>
                        <Check className="w-3 h-3" />
                      </span>
                      <span className={feature.included ? 'text-white/70' : 'text-white/30'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.ctaLink}
                  className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl 
                            font-semibold text-sm transition-all duration-300
                            ${plan.popular 
                              ? 'btn-primary' 
                              : 'btn-secondary'}`}
                >
                  {plan.ctaText}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 border-t border-white/[0.06]">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-white/40">
              Everything you need to know about our pricing
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate/20 flex items-center justify-center">
                    <HelpCircle className="w-4 h-4 text-slate-light" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh-strong" />
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white">
              Still have questions?
            </h2>
            <p className="mt-4 text-white/50">
              Our team is here to help. Reach out and we'll get back to you within 24 hours.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Contact Sales
              </Link>
              <Link to="/help" className="btn-secondary">
                Visit Help Center
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

